import { useState, useEffect, useRef } from 'react'
import { Send, Mail, AlertCircle, Loader2, Users, CheckSquare, Square, Info, Plus, X, Zap, Search, ChevronDown, ChevronUp, ChevronRight, Building2, Briefcase, MapPin, Phone, Linkedin, UserPlus, Trash2, FileText, Paperclip, File, Eye, Clock, Calendar, BarChart3, Globe } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api, { getMyProspects, scheduleEmail, scheduleBulkEmails } from '../../services/api'
import toast from 'react-hot-toast'
import AddContactModal from './AddContactModal'
import EmailPreviewModal from './EmailPreviewModal'
import ConnectGmailButton from './ConnectGmailButton'
import '../../styles/communication.css'
import '../../styles/email-settings.css'

// Clé pour le localStorage
const DRAFT_STORAGE_KEY = 'email_draft'

// Charger le brouillon depuis localStorage
const loadDraft = () => {
  try {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.error('Error loading draft:', e)
  }
  return { to: '', subject: '', body: '' }
}

// Effacer le brouillon
const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY)
  } catch (e) {
    console.error('Error clearing draft:', e)
  }
}

export default function SendEmailView() {
  const queryClient = useQueryClient()
  const bodyRef = useRef(null)
  const [formData, setFormData] = useState(loadDraft)
  const [sending, setSending] = useState(false)
  const [emailStatus, setEmailStatus] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [selectedRecipients, setSelectedRecipients] = useState([])
  const [bulkMode, setBulkMode] = useState(false)
  const [sendingProgress, setSendingProgress] = useState(null)
  const [manualEmail, setManualEmail] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [contactsCollapsed, setContactsCollapsed] = useState(false)
  const [expandedContacts, setExpandedContacts] = useState(new Set())
  // États pour les sections collapsables
  const [modeCollapsed, setModeCollapsed] = useState(false)
  const [optionsCollapsed, setOptionsCollapsed] = useState(false)
  const [variablesCollapsed, setVariablesCollapsed] = useState(false)
  const [addEmailCollapsed, setAddEmailCollapsed] = useState(false)
  const [recipientsCollapsed, setRecipientsCollapsed] = useState(false)
  const [filters, setFilters] = useState({
    seniority: '',
    jobTitle: '',
    industry: '',
    country: '',
    department: ''
  })
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [showTemplates, setShowTemplates] = useState(true)
  const [templates, setTemplates] = useState([])
  const [attachments, setAttachments] = useState([])
  const [uploadingFile, setUploadingFile] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [scheduleMode, setScheduleMode] = useState(false)
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [enableTracking, setEnableTracking] = useState(true)

  // Sauvegarder le brouillon dans localStorage à chaque modification
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData))
    } catch (e) {
      console.error('Error saving draft:', e)
    }
  }, [formData])

  // Récupérer les prospects (exactement comme MyProspects.jsx)
  const { data, isLoading: prospectsLoading, isError, error: prospectsError } = useQuery({
    queryKey: ['myProspects'],
    queryFn: getMyProspects,
  })

  // Extraire les prospects et filtrer uniquement ceux avec email
  const prospects = data?.prospects || []
  const allContacts = prospects.filter(p => p.primaryEmail) || []
  
  // Extraire les valeurs uniques pour les filtres
  const uniqueValues = {
    seniorities: [...new Set(allContacts.map(c => c.seniority).filter(Boolean))].sort(),
    jobTitles: [...new Set(allContacts.map(c => c.jobTitle).filter(Boolean))].sort(),
    industries: [...new Set(allContacts.map(c => c.companyIndustry).filter(Boolean))].sort(),
    countries: [...new Set(allContacts.map(c => c.country).filter(Boolean))].sort(),
    departments: [...new Set(allContacts.map(c => c.departments).filter(Boolean))].sort()
  }

  // Filtrer les contacts selon la recherche ET les filtres avancés
  const contacts = allContacts.filter(contact => {
    // Filtre de recherche textuelle
    if (contactSearch.trim()) {
      const searchLower = contactSearch.toLowerCase()
      const matchesSearch = (
        contact.firstName?.toLowerCase().includes(searchLower) ||
        contact.lastName?.toLowerCase().includes(searchLower) ||
        contact.fullName?.toLowerCase().includes(searchLower) ||
        contact.primaryEmail?.toLowerCase().includes(searchLower) ||
        contact.primaryPhone?.toLowerCase().includes(searchLower) ||
        contact.companyName?.toLowerCase().includes(searchLower) ||
        contact.jobTitle?.toLowerCase().includes(searchLower) ||
        contact.country?.toLowerCase().includes(searchLower) ||
        contact.seniority?.toLowerCase().includes(searchLower) ||
        contact.departments?.toLowerCase().includes(searchLower) ||
        contact.companyIndustry?.toLowerCase().includes(searchLower) ||
        contact.linkedinProfile?.toLowerCase().includes(searchLower)
      )
      if (!matchesSearch) return false
    }

    // Filtres avancés
    if (filters.seniority && contact.seniority !== filters.seniority) return false
    if (filters.jobTitle && contact.jobTitle !== filters.jobTitle) return false
    if (filters.industry && contact.companyIndustry !== filters.industry) return false
    if (filters.country && contact.country !== filters.country) return false
    if (filters.department && contact.departments !== filters.department) return false

    return true
  })

  // Compter les filtres actifs
  const activeFiltersCount = Object.values(filters).filter(Boolean).length

  // Vérifier le statut email au chargement
  useEffect(() => {
    checkEmailStatus()
  }, [])

  const checkEmailStatus = async () => {
    try {
      const response = await api.get('/api/communication/email/status')
      setEmailStatus(response.data)
    } catch (error) {
      console.error('Failed to check email status:', error)
      toast.error("Impossible de vérifier la configuration d'envoi d'emails")
    } finally {
      setLoadingStatus(false)
    }
  }

  const updateProvider = async (provider) => {
    try {
      const response = await api.patch('/api/communication/email/provider', { provider })
      setEmailStatus(response.data)
      toast.success("Mode d'envoi mis à jour")
    } catch (error) {
      toast.error(error?.response?.data?.error || "Impossible de changer le mode d'envoi")
    }
  }

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files)
    
    for (const file of files) {
      // Vérifier la taille (25MB max)
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 25MB)`)
        continue
      }

      setUploadingFile(true)
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const response = await api.post('/api/communication/attachments/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.data.success) {
          setAttachments(prev => [...prev, response.data.file])
          toast.success(`${file.name} ajouté`)
        }
      } catch (error) {
        toast.error(`Erreur lors de l'upload de ${file.name}`)
      } finally {
        setUploadingFile(false)
      }
    }
    
    // Reset input
    e.target.value = ''
  }

  const handleRemoveAttachment = async (fileId) => {
    try {
      await api.delete(`/api/communication/attachments/${fileId}`)
      setAttachments(prev => prev.filter(att => att.id !== fileId))
      toast.success('Fichier supprimé')
    } catch (error) {
      toast.error('Erreur lors de la suppression')
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const insertBodyText = (text) => {
    if (!text) return
    if (bodyRef.current) {
      const start = bodyRef.current.selectionStart
      const end = bodyRef.current.selectionEnd
      const current = formData.body || ''
      const next = current.substring(0, start) + text + current.substring(end)
      setFormData(prev => ({ ...prev, body: next }))
      setTimeout(() => {
        bodyRef.current.selectionStart = bodyRef.current.selectionEnd = start + text.length
        bodyRef.current.focus()
      }, 0)
    } else {
      setFormData(prev => ({ ...prev, body: (prev.body || '') + text }))
    }
  }

  const handleInsertTrackedLink = () => {
    const url = window.prompt('URL du lien à suivre :')
    if (!url) return
    const label = window.prompt('Texte du lien (optionnel) :', url) || url
    insertBodyText(`<a href="${url}">${label}</a>`)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Mode envoi en masse
    if (bulkMode && selectedRecipients.length > 0) {
      handleBulkSend()
      return
    }

    // Mode envoi simple
    if (!formData.to || !formData.subject || !formData.body) {
      toast.error('Veuillez remplir tous les champs')
      return
    }

    // Mode planification
    if (scheduleMode) {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Veuillez sélectionner une date et une heure')
        return
      }
      
      // Parser le format européen dd/mm/yyyy
      const dateParts = scheduledDate.split('/')
      if (dateParts.length !== 3) {
        toast.error('Format de date invalide. Utilisez jj/mm/aaaa')
        return
      }
      const [day, month, year] = dateParts
      
      // Parser l'heure hh:mm
      const timeParts = scheduledTime.split(':')
      if (timeParts.length < 2) {
        toast.error('Format d\'heure invalide. Utilisez hh:mm')
        return
      }
      const [hours, minutes] = timeParts
      
      // Créer la date ISO pour l'API
      const scheduledAt = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`
      const scheduledDateTime = new Date(scheduledAt)
      
      if (isNaN(scheduledDateTime.getTime())) {
        toast.error('Date ou heure invalide')
        return
      }
      
      if (scheduledDateTime <= new Date()) {
        toast.error('La date de planification doit être dans le futur')
        return
      }

      setSending(true)
      try {
        const result = await scheduleEmail({
          recipientEmail: formData.to,
          subject: formData.subject,
          body: formData.body,
          scheduledAt,
          timezone
        })
        
        if (result.success) {
          toast.success(`Email planifié pour le ${day}/${month}/${year} à ${hours}:${minutes} (${timezone})`)
          setFormData({ to: '', subject: '', body: '' })
          clearDraft()
          setScheduledDate('')
          setScheduledTime('')
          setScheduleMode(false)
          queryClient.invalidateQueries(['scheduledEmails'])
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la planification')
      } finally {
        setSending(false)
      }
      return
    }

    setSending(true)

    try {
      const emailData = {
        ...formData,
        attachments: attachments,
        enableTracking: enableTracking
      }
      
      const response = await api.post('/api/communication/send-email', emailData)
      
      if (response.data.success) {
        toast.success('Email envoyé avec succès')
        setFormData({ to: '', subject: '', body: '' })
        clearDraft()
        setAttachments([])
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'envoi')
      }
    } catch (error) {
      console.error('Failed to send email:', error)
      const errorMsg = error?.response?.data?.error || 'Erreur lors de l\'envoi de l\'email'
      toast.error(errorMsg)
    } finally {
      setSending(false)
    }
  }

  const handleBulkSend = async () => {
    if (!formData.subject || !formData.body) {
      toast.error('Veuillez remplir le sujet et le message')
      return
    }

    if (selectedRecipients.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire')
      return
    }

    if (selectedRecipients.length > 100) {
      toast.error('Maximum 100 destinataires par envoi')
      return
    }

    // Mode planification en masse
    if (scheduleMode) {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Veuillez sélectionner une date et une heure')
        return
      }
      
      // Parser le format européen dd/mm/yyyy
      const dateParts = scheduledDate.split('/')
      if (dateParts.length !== 3) {
        toast.error('Format de date invalide. Utilisez jj/mm/aaaa')
        return
      }
      const [day, month, year] = dateParts
      
      // Parser l'heure hh:mm
      const timeParts = scheduledTime.split(':')
      if (timeParts.length < 2) {
        toast.error('Format d\'heure invalide. Utilisez hh:mm')
        return
      }
      const [hours, minutes] = timeParts
      
      // Créer la date ISO pour l'API
      const scheduledAt = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`
      const scheduledDateTime = new Date(scheduledAt)
      
      if (isNaN(scheduledDateTime.getTime())) {
        toast.error('Date ou heure invalide')
        return
      }
      
      if (scheduledDateTime <= new Date()) {
        toast.error('La date de planification doit être dans le futur')
        return
      }

      const confirmed = window.confirm(
        `Vous allez planifier ${selectedRecipients.length} email(s) pour le ${day}/${month}/${year} à ${hours}:${minutes}.\n\nConfirmer ?`
      )
      if (!confirmed) return

      setSending(true)
      try {
        const result = await scheduleBulkEmails(
          selectedRecipients,
          formData.subject,
          formData.body,
          scheduledAt,
          null,
          timezone
        )
        
        if (result.success) {
          toast.success(`${result.created} emails planifiés pour le ${day}/${month}/${year} à ${hours}:${minutes} (${timezone})`)
          setFormData({ to: '', subject: '', body: '' })
          clearDraft()
          setSelectedRecipients([])
          setScheduledDate('')
          setScheduledTime('')
          setScheduleMode(false)
          setBulkMode(false)
          queryClient.invalidateQueries(['scheduledEmails'])
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la planification')
      } finally {
        setSending(false)
      }
      return
    }

    const confirmed = window.confirm(
      `Vous allez envoyer cet email à ${selectedRecipients.length} destinataire(s).\n\n` +
      `Temps estimé : ~${selectedRecipients.length * 2}s\n\n` +
      `Confirmer l'envoi ?`
    )

    if (!confirmed) return

    setSending(true)
    setSendingProgress({ current: 0, total: selectedRecipients.length })

    try {
      const response = await api.post('/api/communication/send-bulk', {
        recipients: selectedRecipients,
        subject: formData.subject,
        body: formData.body,
        attachments: attachments,
        enableTracking: enableTracking
      })

      if (response.data.success) {
        const { results } = response.data
        toast.success(
          `Envoi terminé !\n✓ ${results.sent} envoyés\n✗ ${results.failed} échecs`,
          { duration: 5000 }
        )
        
        // Reset
        setFormData({ to: '', subject: '', body: '' })
        clearDraft()
        setSelectedRecipients([])
        setAttachments([])
        setBulkMode(false)
      }
    } catch (error) {
      console.error('Bulk send failed:', error)
      toast.error('Erreur lors de l\'envoi en masse')
    } finally {
      setSending(false)
      setSendingProgress(null)
    }
  }

  const addManualEmail = () => {
    const email = manualEmail.trim()
    
    if (!email) {
      toast.error('Veuillez entrer une adresse email')
      return
    }

    if (!email.includes('@')) {
      toast.error('Adresse email invalide')
      return
    }

    // Permet les doublons - ajoute toujours
    const recipient = {
      email,
      firstName: '',
      lastName: '',
      company: '',
      position: ''
    }

    setSelectedRecipients(prev => [...prev, recipient])
    setManualEmail('')
  }

  const removeRecipient = (index) => {
    setSelectedRecipients(prev => prev.filter((_, i) => i !== index))
  }

  const toggleContact = (contact) => {
    // Permet d'ajouter plusieurs fois le même contact
    const email = contact.primaryEmail || contact.email
    const recipient = {
      email: email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      company: contact.companyName || contact.company,
      position: contact.jobTitle || contact.position
    }
    setSelectedRecipients(prev => [...prev, recipient])
  }

  const toggleExpandContact = (contactId) => {
    const newExpanded = new Set(expandedContacts)
    if (newExpanded.has(contactId)) {
      newExpanded.delete(contactId)
    } else {
      newExpanded.add(contactId)
    }
    setExpandedContacts(newExpanded)
  }

  const clearAllRecipients = () => {
    setSelectedRecipients([])
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  const removeFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }))
  }

  const clearAllFilters = () => {
    setFilters({
      seniority: '',
      jobTitle: '',
      industry: '',
      country: '',
      department: ''
    })
    setContactSearch('')
  }

  const handleContactAdded = () => {
    // Rafraîchir la liste des prospects
    queryClient.invalidateQueries(['myProspects'])
  }

  // Charger les templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get('/api/email-templates')
        setTemplates(response.data.templates || [])
      } catch (error) {
        console.error('Erreur chargement templates:', error)
      }
    }
    fetchTemplates()
  }, [])

  const handleLoadTemplate = (template) => {
    setFormData({
      ...formData,
      subject: template.subject,
      body: template.body
    })
    setShowTemplates(false)
    toast.success(`Template "${template.name}" chargé !`)
    
    // Incrémenter le compteur d'utilisation
    api.post(`/api/email-templates/${template.id}/use`).catch(console.error)
  }

  const deleteContact = async (contactId, contactEmail) => {
    if (!confirm(`Supprimer le contact ${contactEmail} ?`)) {
      return
    }

    try {
      await api.delete(`/api/prospects/${contactId}`)
      toast.success('Contact supprimé')
      queryClient.invalidateQueries(['myProspects'])
      
      // Retirer des destinataires sélectionnés si présent
      setSelectedRecipients(prev => prev.filter(r => r.email !== contactEmail))
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression')
    }
  }

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      body: prev.body + `{{${variable}}}`
    }))
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (loadingStatus) {
    return (
      <div className="comm-loading-state">
        <Loader2 size={48} className="comm-spinner" />
        <p>Vérification de la configuration d'envoi...</p>
      </div>
    )
  }

  const selectedProvider = emailStatus?.selectedProvider || 'gmail'
  const providerInfo = emailStatus?.providers?.[selectedProvider]
  const providerReady = providerInfo?.available === true

  // Si le provider sélectionné n'est pas prêt
  if (!providerReady) {
    const smtpAvailable = emailStatus?.providers?.smtp?.available === true
    return (
      <div className="comm-empty-state comm-warning">
        <AlertCircle size={64} />
        <h3>Envoi email non disponible</h3>
        <p>
          {selectedProvider === 'gmail'
            ? 'Vous devez connecter votre compte Gmail ou basculer sur SMTP.'
            : 'SMTP n’est pas configuré sur le serveur.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              className="comm-btn comm-btn-secondary"
              onClick={() => updateProvider('gmail')}
              disabled={selectedProvider === 'gmail'}
            >
              Utiliser Gmail
            </button>
            <button
              type="button"
              className="comm-btn comm-btn-secondary"
              onClick={() => updateProvider('smtp')}
              disabled={!smtpAvailable || selectedProvider === 'smtp'}
              title={!smtpAvailable ? 'SMTP non configuré (MAILER_DSN)' : undefined}
            >
              Utiliser SMTP
            </button>
          </div>
          {selectedProvider === 'gmail' && (
            <ConnectGmailButton />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="email-composer-layout">
      {/* Composer gauche */}
      <div className="email-composer-main">
        <form onSubmit={handleSubmit} className="email-form">
          {/* Provider */}
          <div className="email-field">
            <label className="email-field-label">Envoi via</label>
            <select
              value={selectedProvider}
              onChange={(e) => updateProvider(e.target.value)}
              className="email-field-input"
            >
              <option value="gmail">Gmail</option>
              <option value="smtp" disabled={emailStatus?.providers?.smtp?.available !== true}>SMTP</option>
            </select>
          </div>

          {/* De (expéditeur) */}
          <div className="email-field email-field-readonly">
            <label className="email-field-label">De</label>
            <div className="email-field-value">
              {emailStatus?.providers?.gmail?.email || ''}
              <span style={{ opacity: 0.8, marginLeft: '0.5rem' }}>
                ({selectedProvider === 'gmail' ? 'Gmail' : 'SMTP'})
              </span>
            </div>
          </div>

          {/* Destinataire (mode simple uniquement) */}
          {!bulkMode && (
            <div className="email-field">
              <label className="email-field-label">À</label>
              <input
                type="email"
                value={formData.to}
                onChange={(e) => handleChange('to', e.target.value)}
                placeholder="Destinataire"
                required
                className="email-field-input"
              />
            </div>
          )}

          {/* Info mode bulk */}
          {bulkMode && selectedRecipients.length > 0 && (
            <div className="email-field email-field-info">
              <label className="email-field-label">À</label>
              <div className="email-field-value">
                {selectedRecipients.length} destinataire(s) sélectionné(s)
              </div>
            </div>
          )}

          {/* Sujet */}
          <div className="email-field">
            <label className="email-field-label">Objet</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Objet du message"
              required
              className="email-field-input"
            />
          </div>

          {/* Corps du message */}
          <div className="email-body">
            <textarea
              ref={bodyRef}
              value={formData.body}
              onChange={(e) => handleChange('body', e.target.value)}
              placeholder={bulkMode ? "Composez votre message...\n\nUtilisez {{prenom}}, {{nom}}, {{entreprise}}, {{poste}} pour personnaliser." : "Composez votre message..."}
              required
              className="email-body-textarea"
            />
            <div className="email-body-footer">
              <span className="email-body-hint">
                {bulkMode ? "Variables : {{prenom}}, {{nom}}, {{entreprise}}, {{poste}}" : "HTML supporté"}
              </span>
              <button
                type="button"
                onClick={handleInsertTrackedLink}
                style={{
                  background: 'rgb(var(--rgb-brand) / 0.1)',
                  border: '1px solid rgb(var(--rgb-brand) / 0.3)',
                  color: 'rgb(var(--rgb-brand))',
                  padding: '0.3rem 0.6rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                Ajouter un lien suivi
              </button>
            </div>
          </div>

          {/* Pièces jointes */}
          <div className="email-attachments-section">
            <div className="attachments-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Paperclip size={16} />
                <span>Pièces jointes</span>
                {attachments.length > 0 && (
                  <span style={{ 
                    background: 'rgb(var(--rgb-brand) / 0.2)',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'rgb(var(--rgb-brand))'
                  }}>
                    {attachments.length}
                  </span>
                )}
              </div>
              <label className="upload-btn">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  style={{ display: 'none' }}
                />
                <Plus size={16} />
                {uploadingFile ? 'Upload...' : 'Ajouter'}
              </label>
            </div>

            {/* Liste des fichiers */}
            {attachments.length > 0 && (
              <div className="attachments-list">
                {attachments.map(file => (
                  <div key={file.id} className="attachment-item">
                    <div className="attachment-info">
                      <File size={16} style={{ color: 'rgb(var(--rgb-brand))' }} />
                      <div className="attachment-details">
                        <div className="attachment-name">{file.name}</div>
                        <div className="attachment-size">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(file.id)}
                      className="attachment-remove"
                      title="Supprimer"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Planification - inputs date/heure format européen */}
          {scheduleMode && (
            <div className="schedule-inputs">
              <div className="schedule-field">
                <Calendar size={14} />
                <input
                  type="text"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  placeholder="jj/mm/aaaa"
                  pattern="\d{2}/\d{2}/\d{4}"
                  className="schedule-input"
                />
              </div>
              <div className="schedule-field">
                <Clock size={14} />
                <input
                  type="text"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  placeholder="hh:mm"
                  pattern="\d{2}:\d{2}"
                  className="schedule-input"
                />
              </div>
              <div className="schedule-field" style={{ minWidth: '140px' }}>
                <Globe size={14} />
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="schedule-input"
                  style={{ background: 'transparent', border: 'none', color: 'inherit', width: '100%', cursor: 'pointer' }}
                >
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">New York</option>
                  <option value="America/Los_Angeles">Los Angeles</option>
                  <option value="Europe/London">London</option>
                  <option value="Asia/Tokyo">Tokyo</option>
                  <option value={Intl.DateTimeFormat().resolvedOptions().timeZone}>{Intl.DateTimeFormat().resolvedOptions().timeZone} (Local)</option>
                </select>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="email-actions">
            {sendingProgress && (
              <div className="sending-progress">
                <Loader2 size={18} className="spinning" />
                <span>Envoi en cours... ({sendingProgress.current}/{sendingProgress.total})</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              disabled={!formData.subject && !formData.body}
              className="email-preview-btn"
            >
              <Eye size={18} />
              Prévisualiser
            </button>
            <button
              type="button"
              onClick={() => setScheduleMode(!scheduleMode)}
              className={`email-preview-btn ${scheduleMode ? 'active' : ''}`}
            >
              <Clock size={18} />
              {scheduleMode ? 'Planification activée' : 'Planifier'}
            </button>
            <button
              type="submit"
              disabled={sending || (bulkMode && selectedRecipients.length === 0)}
              className={`email-send-btn ${sending ? 'loading' : ''} ${scheduleMode ? 'schedule' : ''}`}
            >
              {sending ? (
                <>
                  <Loader2 size={18} className="spinning" />
                  {scheduleMode ? 'Planification...' : 'Envoi...'}
                </>
              ) : scheduleMode ? (
                <>
                  <Clock size={18} />
                  Planifier {bulkMode ? `${selectedRecipients.length} email(s)` : ''}
                </>
              ) : bulkMode ? (
                <>
                  <Send size={18} />
                  Envoyer à {selectedRecipients.length} destinataire(s)
                </>
              ) : (
                <>
                  <Send size={18} />
                  Envoyer
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de prévisualisation */}
      <EmailPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        subject={formData.subject}
        body={formData.body}
        recipient={bulkMode && selectedRecipients.length > 0 ? {
          firstName: selectedRecipients[0].firstName,
          lastName: selectedRecipients[0].lastName,
          company: selectedRecipients[0].company,
          position: selectedRecipients[0].position,
          email: selectedRecipients[0].email
        } : null}
      />

      {/* Sidebar droite */}
      <div className="email-sidebar">
        {/* Mode Switch */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => setModeCollapsed(!modeCollapsed)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Mode d'envoi</h3>
            {modeCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </div>
          <div className={`section-content ${modeCollapsed ? 'collapsed' : 'expanded'}`}>
            <div className="mode-switch">
              <button
                type="button"
                className={`mode-btn ${!bulkMode ? 'active' : ''}`}
                onClick={() => {
                  setBulkMode(false)
                  setSelectedRecipients([])
                }}
              >
                <Mail size={18} />
                Simple
              </button>
              <button
                type="button"
                className={`mode-btn ${bulkMode ? 'active' : ''}`}
                onClick={() => setBulkMode(true)}
              >
                <Users size={18} />
                En masse
              </button>
            </div>
          </div>
        </div>

        {/* Options d'envoi */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => setOptionsCollapsed(!optionsCollapsed)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Options</h3>
            {optionsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </div>
          <div className={`section-content ${optionsCollapsed ? 'collapsed' : 'expanded'}`}>
            <div style={{ padding: '0.5rem 0' }}>
              <label 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: enableTracking ? 'rgb(var(--rgb-brand) / 0.08)' : 'rgb(var(--rgb-shade) / 0.2)',
                  border: enableTracking ? '1px solid rgb(var(--rgb-brand) / 0.3)' : '1px solid rgb(var(--rgb-ink) / 0.05)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div 
                  onClick={() => setEnableTracking(!enableTracking)}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: enableTracking ? '2px solid rgb(var(--rgb-brand))' : '2px solid var(--color-text-muted)',
                    background: enableTracking ? 'rgb(var(--rgb-brand))' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    flexShrink: 0
                  }}
                >
                  {enableTracking && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    color: enableTracking ? 'rgb(var(--rgb-brand))' : 'var(--color-text-secondary)',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}>
                    <BarChart3 size={16} />
                    Activer le tracking
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--color-text-muted)',
                    marginTop: '0.25rem'
                  }}>
                    Suivre les ouvertures et clics
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Templates */}
        {templates.length > 0 && (
          <div className="sidebar-section">
            <div 
              className="sidebar-header"
              onClick={() => setShowTemplates(!showTemplates)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Templates</h3>
                <span style={{ 
                  background: 'rgb(var(--rgb-brand) / 0.2)',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'rgb(var(--rgb-brand))'
                }}>
                  {templates.length}
                </span>
              </div>
              {showTemplates ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            
            {/* Liste templates */}
            <div className={`templates-section-content ${showTemplates ? 'expanded' : 'collapsed'}`}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                maxHeight: '300px',
                overflow: 'auto',
                padding: '0.5rem 0'
              }}>
                {templates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => handleLoadTemplate(template)}
                    style={{
                      padding: '0.75rem',
                      background: 'rgb(var(--rgb-shade) / 0.2)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.05)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgb(var(--rgb-brand) / 0.08)'
                      e.currentTarget.style.borderColor = 'rgb(var(--rgb-brand) / 0.2)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgb(var(--rgb-shade) / 0.2)'
                      e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.05)'
                    }}
                  >
                    <div style={{ 
                      fontWeight: 500, 
                      marginBottom: '0.375rem', 
                      color: 'var(--color-text)',
                      fontSize: '0.8125rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      flexWrap: 'wrap'
                    }}>
                      <FileText size={14} style={{ color: 'rgb(var(--rgb-brand))', flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>{template.name}</span>
                      {template.category && (
                        <span style={{
                          padding: '0.125rem 0.375rem',
                          background: 'rgb(var(--rgb-brand) / 0.15)',
                          color: 'rgb(var(--rgb-brand))',
                          borderRadius: '3px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          flexShrink: 0
                        }}>
                          {template.category}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--color-text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      paddingLeft: '1.375rem'
                    }}>
                      {template.subject}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Variables disponibles */}
        <div className="sidebar-section">
          <div 
            className="sidebar-header"
            onClick={() => setVariablesCollapsed(!variablesCollapsed)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Variables</h3>
              <Info size={16} />
            </div>
            {variablesCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </div>
          <div className={`section-content ${variablesCollapsed ? 'collapsed' : 'expanded'}`}>
            <div className="variables-list">
              <button onClick={() => insertVariable('prenom')} className="variable-btn">
                {'{{prenom}}'}
              </button>
              <button onClick={() => insertVariable('nom')} className="variable-btn">
                {'{{nom}}'}
              </button>
              <button onClick={() => insertVariable('entreprise')} className="variable-btn">
                {'{{entreprise}}'}
              </button>
              <button onClick={() => insertVariable('poste')} className="variable-btn">
                {'{{poste}}'}
              </button>
            </div>
          </div>
        </div>

        {/* Ajout manuel d'email (mode bulk) */}
        {bulkMode && (
          <div className="sidebar-section">
            <div 
              className="sidebar-header"
              onClick={() => setAddEmailCollapsed(!addEmailCollapsed)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Ajouter un email</h3>
              {addEmailCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </div>
            <div className={`section-content ${addEmailCollapsed ? 'collapsed' : 'expanded'}`}>
              <div className="manual-add-form">
                <input
                  type="email"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addManualEmail())}
                  placeholder="email@exemple.com"
                  className="manual-add-input"
                />
                <button
                  type="button"
                  onClick={addManualEmail}
                  disabled={!manualEmail.trim()}
                  className="manual-add-btn"
                >
                  <Plus size={18} />
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Destinataires sélectionnés (mode bulk) */}
        {bulkMode && selectedRecipients.length > 0 && (
          <div className="sidebar-section">
            <div 
              className="sidebar-header"
              onClick={() => setRecipientsCollapsed(!recipientsCollapsed)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Destinataires ({selectedRecipients.length})</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button onClick={(e) => { e.stopPropagation(); clearAllRecipients(); }} className="toggle-all-btn">
                  <X size={18} />
                </button>
                {recipientsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </div>
            </div>
            <div className={`section-content ${recipientsCollapsed ? 'collapsed' : 'expanded'}`}>
              <div className="selected-recipients-list">
                {selectedRecipients.map((recipient, index) => (
                  <div key={index} className="selected-recipient">
                    <div className="selected-recipient-info">
                      {(recipient.firstName || recipient.lastName) && (
                        <span className="selected-recipient-name">
                          {[recipient.firstName, recipient.lastName].filter(Boolean).join(' ')}
                        </span>
                      )}
                      <span className="selected-recipient-email">
                        {recipient.email}
                      </span>
                    </div>
                    <button
                      onClick={() => removeRecipient(index)}
                      className="remove-recipient-btn"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Contacts disponibles (mode bulk) */}
        {bulkMode && (
          <div className="sidebar-section">
            <div 
              className="sidebar-header"
              onClick={() => setContactsCollapsed(!contactsCollapsed)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h3 style={{ color: 'rgb(var(--rgb-brand))' }}>Contacts</h3>
                <span style={{ 
                  background: 'rgb(var(--rgb-brand) / 0.2)',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'rgb(var(--rgb-brand))'
                }}>
                  {contacts.length}{(contactSearch || activeFiltersCount > 0) && ` / ${allContacts.length}`}
                </span>
                {activeFiltersCount > 0 && (
                  <span style={{ 
                    background: 'rgb(var(--rgb-brand) / 0.3)',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'rgb(var(--rgb-brand))'
                  }}>
                    ● {activeFiltersCount}
                  </span>
                )}
              </div>
              {contactsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </div>
            
            <div className={`contacts-section-content ${contactsCollapsed ? 'collapsed' : 'expanded'}`}>
              <div style={{ padding: '0.5rem 0' }}>
                {/* Bouton Ajouter un contact */}
                <div style={{ padding: '0 0 1rem 0' }}>
                  <button
                onClick={() => setShowAddContactModal(true)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgb(var(--rgb-ink) / 0.02)',
                  border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                  borderRadius: '6px',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'
                  e.currentTarget.style.color = 'var(--color-text)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'
                  e.currentTarget.style.color = 'var(--color-text-secondary)'
                }}
              >
                <UserPlus size={16} />
                Ajouter un contact
              </button>
                </div>

                {/* Filtres avancés */}
                <div className="advanced-filters">
                  <div className="filter-row">
                <select 
                  className="filter-select" 
                  value={filters.seniority}
                  onChange={(e) => handleFilterChange('seniority', e.target.value)}
                >
                  <option value="">Séniorité</option>
                  {uniqueValues.seniorities.map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>

                <select 
                  className="filter-select" 
                  value={filters.jobTitle}
                  onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
                >
                  <option value="">Poste</option>
                  {uniqueValues.jobTitles.map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>

                <select 
                  className="filter-select" 
                  value={filters.industry}
                  onChange={(e) => handleFilterChange('industry', e.target.value)}
                >
                  <option value="">Industrie</option>
                  {uniqueValues.industries.map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>

                <select 
                  className="filter-select" 
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                >
                  <option value="">Pays</option>
                  {uniqueValues.countries.map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>

                <select 
                  className="filter-select" 
                  value={filters.department}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                >
                  <option value="">Département</option>
                  {uniqueValues.departments.map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>
                  </div>

                  {/* Chips des filtres actifs */}
                  {activeFiltersCount > 0 && (
                    <div className="filter-chips">
                      {filters.seniority && (
                        <div className="filter-chip">
                          {filters.seniority}
                          <button onClick={() => removeFilter('seniority')}>
                            <X size={12} />
                          </button>
                        </div>
                      )}
                      {filters.jobTitle && (
                        <div className="filter-chip">
                          {filters.jobTitle}
                          <button onClick={() => removeFilter('jobTitle')}>
                            <X size={12} />
                          </button>
                        </div>
                      )}
                      {filters.industry && (
                        <div className="filter-chip">
                          {filters.industry}
                          <button onClick={() => removeFilter('industry')}>
                            <X size={12} />
                          </button>
                        </div>
                      )}
                      {filters.country && (
                        <div className="filter-chip">
                          {filters.country}
                          <button onClick={() => removeFilter('country')}>
                            <X size={12} />
                          </button>
                        </div>
                      )}
                      {filters.department && (
                        <div className="filter-chip">
                          {filters.department}
                          <button onClick={() => removeFilter('department')}>
                            <X size={12} />
                          </button>
                        </div>
                      )}
                      <button className="clear-filters-btn" onClick={clearAllFilters}>
                        <X size={14} />
                        Tout effacer
                      </button>
                    </div>
                  )}
                </div>

                <div className="contact-search">
                  <div className="contact-search-wrapper">
                    <Search size={16} className="contact-search-icon" />
                    <input
                      type="text"
                      className="contact-search-input"
                      placeholder="Rechercher un contact..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                    />
                    {contactSearch && (
                      <button
                        className="contact-search-clear"
                        onClick={() => setContactSearch('')}
                        title="Effacer la recherche"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="contacts-list">
                  {prospectsLoading ? (
                    <div className="contacts-loading">
                      <Loader2 size={24} className="spinning" />
                      <span>Chargement...</span>
                    </div>
                  ) : isError ? (
                    <div className="contacts-empty" style={{ color: '#ef4444' }}>
                      Erreur: {prospectsError?.response?.data?.error || prospectsError?.message || 'Impossible de charger les prospects'}
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="contacts-empty">
                      {contactSearch 
                        ? `Aucun contact trouvé pour "${contactSearch}"` 
                        : 'Aucun prospect avec email'}
                    </div>
                  ) : (
                    contacts.slice(0, 100).map(contact => {
                  const isExpanded = expandedContacts.has(contact.id)
                  return (
                    <div key={contact.id || contact.primaryEmail} className="contact-item-wrapper">
                      <div
                        className="contact-item"
                        style={{
                          background: isExpanded ? 'rgb(var(--rgb-brand) / 0.03)' : 'transparent'
                        }}
                      >
                        <button
                          className="contact-checkbox"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleContact(contact)
                          }}
                          title="Ajouter aux destinataires"
                        >
                          <Plus size={18} />
                        </button>
                        <div 
                          className="contact-info" 
                          onClick={() => toggleExpandContact(contact.id)}
                          style={{ cursor: 'pointer', flex: 1 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ color: isExpanded ? 'rgb(var(--rgb-brand))' : 'var(--color-text-secondary)', transition: 'all 0.3s' }}>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            <div className="contact-name">
                              {contact.firstName} {contact.lastName}
                            </div>
                          </div>
                          <div className="contact-email">{contact.primaryEmail}</div>
                          {contact.companyName && (
                            <div className="contact-company">{contact.companyName}</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Détails expansibles */}
                      <div
                        style={{
                          maxHeight: isExpanded ? '500px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease, opacity 0.3s ease',
                          opacity: isExpanded ? 1 : 0,
                        }}
                      >
                        <div style={{
                          padding: '1rem 1.25rem 1rem 3rem',
                          background: 'rgb(var(--rgb-shade) / 0.3)',
                          fontSize: '0.8125rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.75rem'
                        }}>
                          {contact.jobTitle && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)' }}>
                              <Briefcase size={14} style={{ color: 'var(--color-text-secondary)' }} />
                              <span>{contact.jobTitle}</span>
                            </div>
                          )}
                          {contact.primaryPhone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)' }}>
                              <Phone size={14} style={{ color: 'var(--color-text-secondary)' }} />
                              <span>{contact.primaryPhone}</span>
                            </div>
                          )}
                          {contact.country && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)' }}>
                              <MapPin size={14} style={{ color: 'var(--color-text-secondary)' }} />
                              <span>{contact.country}</span>
                            </div>
                          )}
                          {contact.companyIndustry && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)' }}>
                              <Building2 size={14} style={{ color: 'var(--color-text-secondary)' }} />
                              <span>{contact.companyIndustry}</span>
                            </div>
                          )}
                          {contact.linkedinProfile && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Linkedin size={14} style={{ color: '#0077b5' }} />
                              <a 
                                href={contact.linkedinProfile} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                style={{ color: '#0077b5', textDecoration: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                LinkedIn
                              </a>
                            </div>
                          )}
                          
                          {/* Bouton supprimer */}
                          <div style={{ 
                            marginTop: '0.5rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid rgb(var(--rgb-ink) / 0.05)'
                          }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                deleteContact(contact.id, contact.primaryEmail)
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                borderRadius: '4px',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '0.8125rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'
                                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)'
                              }}
                            >
                              <Trash2 size={14} />
                              Supprimer ce contact
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                      )
                    })
                  )}
                  {contacts.length > 100 && (
                    <div className="contacts-more">
                      +{contacts.length - 100} autres contacts disponibles
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quota info */}
        <div className="sidebar-section sidebar-footer">
          <div className="quota-info">
            <div className="quota-label">Quota Gmail</div>
            <div className="quota-value">500/jour</div>
            <div className="quota-note">Délai automatique : 2s entre chaque email</div>
          </div>
        </div>
      </div>

      {/* Modal d'ajout de contact */}
      <AddContactModal
        isOpen={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        onContactAdded={handleContactAdded}
      />
    </div>
  )
}
